import React from "react";
import {
	Button,
	ClassNameProps,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	StyleProps,
	useCount,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { MediaAttachment } from "../../../../Utilities";
import { getPreloadMediaAttachments, VisualAttachmentCarousel } from "../VisualAttachmentCarousel";
import styles from "./styles.module.css";

export type MediaAttachmentModalProps = {
	mediaAttachments: Pick<MediaAttachment, "id" | "fileURL" | "fileKey" | "fileContentType">[];
	initialIndex?: number;
	onIndexChange?: (index: number) => void;
	backButtonText?: string;
} & Pick<ModalProps, "ifRef" | "onClose"> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Modal display for media attachments (should only be visual attachments).
 */
export function MediaAttachmentModal({
	mediaAttachments,
	initialIndex = 0,
	onIndexChange,
	backButtonText,
	ifRef,
	onClose,
	className,
	style,
}: MediaAttachmentModalProps) {
	const {
		count: index,
		decrement: decrementIndex,
		increment: incrementIndex,
	} = useCount(initialIndex, { mod: mediaAttachments.length });
	const attachment = mediaAttachments[index];

	const preloadAttachments = React.useMemo(
		() => getPreloadMediaAttachments(mediaAttachments, index),
		[mediaAttachments, index],
	);

	React.useEffect(() => {
		if (onIndexChange) {
			onIndexChange(index);
		}
	}, [onIndexChange, index]);

	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["media-attachment-modal"], className)}
			style={style}
		>
			<If condition={!!backButtonText && !!onClose}>
				<Modal.Header>
					{onClose && (
						<Button variant="link" size="small" onClick={onClose}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
								<Icon.ArrowLeft size={14} /> {backButtonText}
							</Row>
						</Button>
					)}
				</Modal.Header>
			</If>
			<Modal.Body style={{ height: "100%", overflow: "hidden" }}>
				<VisualAttachmentCarousel
					mediaAttachment={attachment}
					preloadMediaAttachments={preloadAttachments}
					index={index}
					incrementIndex={incrementIndex}
					decrementIndex={decrementIndex}
					totalAttachments={mediaAttachments.length}
					maxWidth={800}
					style={{ height: "100%" }}
				/>
			</Modal.Body>
		</Modal>
	);
}

MediaAttachmentModal.useModal = function () {
	const {
		isModalOpen: isMediaAttachmentModalOpen,
		openModal: openMediaAttachmentModal,
		closeModal: closeMediaAttachmentModal,
	} = Modal.useToggle();
	const [mediaAttachmentModalIndex, setMediaAttachmentModalIndex] = React.useState(0);
	const onClickMediaAttachment = React.useCallback(
		(index: number) => {
			setMediaAttachmentModalIndex(index);
			openMediaAttachmentModal();
		},
		[openMediaAttachmentModal],
	);

	return { isMediaAttachmentModalOpen, onClickMediaAttachment, closeMediaAttachmentModal, mediaAttachmentModalIndex };
};
